import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Waypoints, Search } from "lucide-react";
import { JourneyService } from "@services/journey/journeyService";
import { JourneySummary } from "@services/journey/commands/cmdListJourneys";
import { JourneyStepSearchResult } from "@services/journey/commands/cmdSearchJourneySteps";
import IndustryRail from "./IndustryRail";
import JourneyTreeEditor from "./JourneyTreeEditor";
import AddJourneyModal from "./AddJourneyModal";
import "./journeyStepManager.css";

const service = new JourneyService();

export default function JourneyStepManagerPage() {
  const [journeys, setJourneys] = useState<JourneySummary[]>([]);
  // Selected journey lives in the URL (?journey=<id>) so it survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  // null when closed; { industry: null } = add industry; { industry: "X" } = add journey to X.
  const [addModal, setAddModal] = useState<{ industry: string | null } | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<JourneyStepSearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  // Step to scroll to / highlight in the tree; nonce re-triggers on repeat clicks.
  const [target, setTarget] = useState<{ stepId: number; nonce: number } | null>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  const selectedId = useMemo(() => {
    const raw = Number(searchParams.get("journey"));
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  }, [searchParams]);

  const selectJourney = useCallback(
    (id: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("journey", String(id));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const loadJourneys = useCallback(async () => {
    setJourneys(await service.listJourneys());
  }, []);

  useEffect(() => {
    document.title = "Journey Step Manager";
    loadJourneys();
  }, [loadJourneys]);

  // Default the selection once journeys load if the URL points at nothing valid.
  useEffect(() => {
    if (journeys.length === 0) return;
    if (selectedId != null && journeys.some((j) => j.id === selectedId)) return;
    const preferred =
      journeys.find((j) => (j.industry || "").toLowerCase().includes("education") && !j.is_master) ??
      journeys[0];
    if (preferred) selectJourney(preferred.id);
  }, [journeys, selectedId, selectJourney]);

  const selected = useMemo(
    () => journeys.find((j) => j.id === selectedId) ?? null,
    [journeys, selectedId],
  );

  const handleCreateJourney = async (name: string, industry: string) => {
    const created = await service.createJourney({ name, industry });
    await loadJourneys();
    selectJourney(created.id);
  };

  // Debounced cross-journey step search.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    let active = true;
    const handle = setTimeout(async () => {
      try {
        const hits = await service.searchSteps(q);
        if (active) {
          setResults(hits);
          setShowResults(true);
        }
      } catch {
        if (active) setResults([]);
      }
    }, 200);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [query]);

  useEffect(() => {
    if (!showResults) return;
    const close = (e: MouseEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) setShowResults(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showResults]);

  const openResult = (hit: JourneyStepSearchResult) => {
    selectJourney(hit.journey_id);
    setTarget((prev) => ({ stepId: hit.id, nonce: (prev?.nonce ?? 0) + 1 }));
    setShowResults(false);
  };

  const groupedResults = useMemo(() => {
    const groups = new Map<number, { name: string; hits: JourneyStepSearchResult[] }>();
    for (const hit of results) {
      const group = groups.get(hit.journey_id) ?? { name: hit.journey_name, hits: [] };
      group.hits.push(hit);
      groups.set(hit.journey_id, group);
    }
    return [...groups.values()];
  }, [results]);

  return (
    <div className="jsm-root">
      <div className="jsm-toolbar">
        <div className="jsm-toolbar-title">
          <Waypoints size={20} />
          Journey Step Manager
        </div>
        <div className="jsm-search-wrap" ref={searchWrapRef}>
          <Search className="jsm-search-icon" size={15} />
          <input
            className="jsm-search"
            placeholder="Search steps across all journeys…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setShowResults(true)}
          />
          {showResults && (
            <div className="jsm-search-results">
              {groupedResults.length === 0 ? (
                <div className="jsm-search-empty">No steps match “{query.trim()}”.</div>
              ) : (
                groupedResults.map((group) => (
                  <div key={group.name} className="jsm-search-group">
                    <div className="jsm-search-group-name">{group.name}</div>
                    {group.hits.map((hit) => (
                      <button
                        key={hit.id}
                        className="jsm-search-hit"
                        onClick={() => openResult(hit)}
                      >
                        <span className="jsm-search-hit-name">{hit.name}</span>
                        {hit.breadcrumb.length > 0 && (
                          <span className="jsm-search-hit-path">
                            {hit.breadcrumb.join(" › ")}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="jsm-body">
        <IndustryRail
          journeys={journeys}
          selectedId={selectedId}
          onSelect={selectJourney}
          onAddIndustry={() => setAddModal({ industry: null })}
          onAddJourney={(industry) => setAddModal({ industry })}
        />
        {selected ? (
          <JourneyTreeEditor key={selected.id} journey={selected} target={target} />
        ) : (
          <div className="jsm-editor">
            <div className="jsm-stats">Select a journey to manage its steps.</div>
          </div>
        )}
      </div>

      <AddJourneyModal
        show={addModal !== null}
        presetIndustry={addModal?.industry ?? null}
        onClose={() => setAddModal(null)}
        onCreate={handleCreateJourney}
      />
    </div>
  );
}
